// Reads the reason a report share was refused out of a mutation failure. Pure and React-free so the
// parsing is testable on its own.

/** A refusal the server explained, as opposed to one that only has a correlation id. */
export type ReportShareRejection = {
	/** The reason, ready to show. Written by report-service, which owns the copy - never reworded. */
	reason: string;
	/** The addresses the server would not accept, in the order it listed them. */
	rejectedEmails: string[];
	/** The correlation id, for support to find the request. Absent if the message carried none. */
	correlationId?: string;
};

// The marker graphql prefixes onto a refused share, the addresses it lists alongside it, and the
// trailing "(correlationId)" that formatError appends. This literal is the other half of a contract
// with REPORT_SHARE_RECIPIENT_REJECTED in graphql's ReportShareRejectionUtil, and the two packages
// cannot import from each other, so it is repeated rather than shared. Changing it on either side
// fails that side's tests, which is the prompt to change it here too - the cost of getting it wrong
// is that a stated reason silently reverts to an opaque error modal.
const REJECTION_MARKER = 'REPORT_SHARE_RECIPIENT_REJECTED';
const REJECTION = /^ \[([^\]]*)\]: ([\s\S]*?)(?: \(([^()]*)\))?$/;

/**
 * Reads a share refusal out of a mutation error, or returns null when the error is not one of ours -
 * in which case the caller must fall back to the standard error handling rather than swallowing it.
 *
 * The arriving text is Relay's stringified error, so the reason sits somewhere inside a larger
 * message rather than at its start, and Relay's own context follows on later lines.
 */
export const parseServerRejection = (error: string): ReportShareRejection | null => {
	const at = error.indexOf(REJECTION_MARKER);
	if (at === -1) {
		return null;
	}

	const [firstLine] = error.slice(at + REJECTION_MARKER.length).split('\n');
	const matched = firstLine.match(REJECTION);
	if (!matched) {
		return null;
	}

	const [, emails, reason, correlationId] = matched;
	const rejectedEmails = emails
		.split(',')
		.map(email => email.trim())
		.filter(email => email !== '');
	const trimmedReason = reason.trim();
	// A marker with no reason behind it says nothing the user can act on, so it goes to the standard
	// handling - which at least surfaces a correlation id support can search for.
	if (trimmedReason === '') {
		return null;
	}

	return {
		reason: trimmedReason,
		rejectedEmails,
		correlationId: correlationId?.trim() || undefined,
	};
};
